import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useFormValidation from '../../../../../hooks/useFormValidation';
import useProviderFieldConfig from './useProviderFieldConfig';
import {
    NewProviderBody,
    useCreateCarcinogenicProviderMutation,
    useEditCarcinogenicProviderMutation
} from '../../../../../services/carcinogenicApi';
import useAlerts from './useAlerts';
import { enqueueSnackbar } from 'notistack';

export interface ProviderFormData {
    substance: null;
    chemicalAndComercialName: string | null;
    cuit: string | null;
    type: null;
}

const INITIAL_VALUES = {
    substance: '',
    chemicalAndComercialName: '',
    cuit: '',
    type: ''
};

const getInitialData = ({ selectedSubstance, isEditing }: any) => {
    if (!isEditing) return INITIAL_VALUES;
    return {
        substance: selectedSubstance?.id || '',
        chemicalAndComercialName: selectedSubstance?.proveedor?.nombreQuimico || '',
        cuit: selectedSubstance?.proveedor?.cuit || '',
        type: selectedSubstance?.proveedor?.tipo ?? ''
    };
};

const useProviderForm = ({
    selectedSubstance,
    selectedCarcinogenicReportId,
    isEditing,
    handleCloseDrawer
}: {
    selectedCarcinogenicReportId: number | null;
    selectedSubstance: any;
    isEditing: boolean;
    handleCloseDrawer: any;
}) => {
    const initialValues = getInitialData({ selectedSubstance, isEditing });
    const [formData, setFormData] = useState(initialValues);

    const [createNewProvider, { isLoading: isCreatingProvider }] =
        useCreateCarcinogenicProviderMutation();
    const [editProvider, { isLoading: isEditingProvider }] = useEditCarcinogenicProviderMutation();

    const { t } = useTranslation('carcinogenicReport', {
        keyPrefix: 'modules.providersAndBuyers'
    });

    const { showConfirmCreateAlert, showConfirmEditAlert } = useAlerts({
        translate: t
    });

    const {
        errors,
        hasValidationErrors,
        validateForm,
        handleBlurValidation,
        resetValidation,
        setErrors
    } = useFormValidation();

    const { providerFieldsConfig } = useProviderFieldConfig({
        t,
        newProviderFormData: formData,
        handleBlurValidation
    });

    const handleChange = (name: string, value: any) => {
        setErrors((prevState) => ({
            ...prevState,
            [name]: { hasError: false, errorMessage: '' }
        }));
        setFormData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const handleCreateNewProvider = async () => {
        try {
            if (!selectedCarcinogenicReportId || !formData.substance) return;
            if (!validateForm(formData, providerFieldsConfig)) return;
            const adaptedBody: NewProviderBody = {
                nombreQuimico: formData?.chemicalAndComercialName,
                cuit: formData?.cuit,
                tipo: formData?.type
            };

            const response: any = await createNewProvider({
                reportId: selectedCarcinogenicReportId,
                substanceId: Number(formData.substance),
                body: adaptedBody
            });

            if (response?.error) {
                enqueueSnackbar(t('enqueues.create.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(t('enqueues.create.success'), {
                    variant: 'success'
                });
                handleCloseDrawer();
            }
        } catch (error) {
            enqueueSnackbar(t('enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    };

    const handleEditProvider = async () => {
        try {
            if (
                !selectedCarcinogenicReportId ||
                !formData.substance ||
                !selectedSubstance?.proveedor?.id
            )
                return;
            const adaptedBody: NewProviderBody = {
                nombreQuimico: formData?.chemicalAndComercialName,
                cuit: formData?.cuit,
                tipo: formData?.type
            };

            const response: any = await editProvider({
                reportId: selectedCarcinogenicReportId,
                substanceId: Number(formData.substance),
                providerId: selectedSubstance?.proveedor?.id,
                body: adaptedBody
            });
            if (response?.error) {
                enqueueSnackbar(t('enqueues.edit.error'), {
                    variant: 'error'
                });
            } else {
                enqueueSnackbar(t('enqueues.edit.success'), {
                    variant: 'success'
                });
                handleCloseDrawer();
            }
        } catch (error) {
            enqueueSnackbar(t('enqueues.common.somethingWentWrong'), {
                variant: 'error'
            });
        }
    };

    const handleSubmit = () => {
        if (!validateForm(formData, providerFieldsConfig)) return;
        if (isEditing) {
            showConfirmEditAlert({ confirmAction: handleEditProvider });
        } else {
            showConfirmCreateAlert({ confirmAction: handleCreateNewProvider });
        }
    };

    return {
        formData,
        errors,
        providerFieldsConfig,
        isCreatingProvider,
        isEditingProvider,
        handleChange,
        validateForm,
        handleSubmit
    };
};

export default useProviderForm;
